<!-- Footer -->
<div class="schedule-footer-wrapper">
  <div class="schedule-footer"
       data-testid="schedule-footer"
       (touchstart)="onFooterTouchStart($event)"
       (touchend)="onFooterTouchEnd($event)"
       (touchcancel)="onFooterTouchCancel($event)"
       (touchmove)="onFooterTouchMove($event)"
       (mousedown)="onFooterMouseDown($event)"
       (mouseup)="onFooterMouseUp($event)"
       (mouseleave)="onFooterMouseLeave($event)"
       (mousemove)="onFooterMouseMove($event)"
       (click)="onFooterClick($event)">
    <div class="schedule-footer-content">
      <span class="schedule-footer-text">Build Quality Assurance</span>
      <span class="schedule-footer-version">v{{ appVersion }}</span>
    </div>
  </div>
  <div class="schedule-footer-spacer"></div>
</div>

<!-- Support Bottom Sheet -->
<app-bottom-sheet data-testid="support-bottom-sheet"
                  [isOpen]="isSupportSheetOpen"
                  [hasFooter]="false"
                  [headerTemplate]="supportHeaderTemplate"
                  [contentTemplate]="supportContentTemplate"
                  [backgroundClass]="'support-sheet-bg'"
                  (isOpenChange)="onSupportSheetClose($event)">
</app-bottom-sheet>

<ng-template #supportHeaderTemplate>
  <div class="support-header-content">
    <button class="close-icon-btn"
            (click)="onSupportSheetClose(false)"
            data-testid="support-close-icon-btn"
            aria-label="Close"
            type="button">
      <i class="pi pi-times"
         aria-hidden="true"></i>
    </button>
    <h2 class="support-sheet-title">Support</h2>
  </div>
</ng-template>

<ng-template #supportContentTemplate>
  <div class="support-content"
       [class.is-offline]="!isOnline">
    @if (!isOnline) {
      <div class="offline-banner-container"
           data-testid="support-offline-banner">
        <svg class="offline-banner-icon"
             width="16"
             height="16"
             viewBox="0 -960 960 960"
             fill="none"
             xmlns="http://www.w3.org/2000/svg"
             aria-hidden="true">
          <path d="M260-160q-92 0-156-64T40-380q0-77 47.5-137T210-594q3-8 6-15.5t6-16.5L84-764q-11-11-11-28t11-28q11-11 28-11t28 11l680 680q11 11 11.5 27.5T820-84q-11 11-27.5 11.5T764-83l-78-77H260Zm0-80h346L284-562q-2 11-3 21t-1 21h-20q-58 0-99 41t-41 99q0 58 41 99t99 41Zm185-161Zm419 191-58-56q17-14 25.5-32.5T840-340q0-42-29-71t-71-29h-60v-80q0-83-58.5-141.5T480-720q-27 0-52 6.5T380-693l-58-58q35-24 74.5-36.5T480-800q117 0 198.5 81.5T760-520q69 8 114.5 59.5T920-340q0 39-15 72.5T864-210ZM593-479Z"
                fill="currentColor" />
        </svg>
        <span class="offline-banner-message"><strong>You're offline</strong> &ndash; Internet required for these tools</span>
      </div>
    }
    <app-support-card icon="/assets/icons/send.svg"
                      iconAlt="Send"
                      cardTitle="Send Diagnostics"
                      description="Share app details so we can look into the issue."
                      buttonLabel="Send Diagnostics"
                      [loadingLabel]="uploadPercentage > 0 ? 'Sending (' + uploadPercentage + '%)' : 'Preparing Data...'"
                      [isLoading]="isCollectingDiagnostics"
                      [isOffline]="!isOnline"
                      [dimmed]="!isOnline"
                      [disabled]="isCollectingDiagnostics || !isOnline"
                      testId="send-diagnostics"
                      [ariaLabel]="isCollectingDiagnostics ? 'Sending diagnostic data...' : 'Send diagnostics to support'"
                      (action)="onSendDiagnosticsClick()">
    </app-support-card>
    <app-support-card icon="/assets/icons/reset-connection.svg"
                      iconAlt="Send"
                      cardTitle="Reset Connection"
                      description="Refresh saved data and start a new connection."
                      buttonLabel="Reset Connection"
                      loadingLabel="Resetting"
                      [isLoading]="isResettingConnection"
                      [isOffline]="!isOnline"
                      [dimmed]="!isOnline"
                      [disabled]="isResettingConnection || !isOnline"
                      testId="reset-connection"
                      [ariaLabel]="isResettingConnection ? 'Resetting Connection' : 'Reset connection'"
                      (action)="onResetConnectionClick($event)">
    </app-support-card>
    <app-support-card icon="/assets/icons/clear-data.svg"
                      iconAlt="Clear"
                      cardTitle="Clear Device Data"
                      description="Clear this app's data from your device."
                      buttonLabel="Clear Device Data"
                      loadingLabel="Clearing"
                      [isLoading]="isClearingDeviceData"
                      [isOffline]="!isOnline"
                      [dimmed]="!isOnline"
                      [disabled]="isClearingDeviceData || !isOnline"
                      testId="clear-device-data"
                      [ariaLabel]="isClearingDeviceData ? 'Clearing device data' : 'Clear device data'"
                      (action)="onClearDeviceDataClick($event)">
    </app-support-card>
    <app-support-card icon="/assets/icons/sync.svg"
                      iconAlt="Sync"
                      cardTitle="Force Sync"
                      description="Manually sync all data with the server now."
                      buttonLabel="Force Sync"
                      [loadingLabel]="forceSyncProgress > 0 ? 'Syncing (' + forceSyncProgress + '%)' : 'Preparing Sync...'"
                      successLabel="Sync Complete!"
                      [isLoading]="isForceSyncing"
                      [success]="forceSyncSuccess"
                      [isOffline]="!isOnline"
                      [dimmed]="!isOnline"
                      [disabled]="isForceSyncing || !isOnline"
                      testId="force-sync"
                      [ariaLabel]="isForceSyncing ? 'Force sync in progress' : 'Run a manual force sync'"
                      [customTemplate]="forceSyncProgressTemplate"
                      (action)="onForceSyncClick()">
    </app-support-card>
    @if (isNonProductionEnvironment) {
      <app-support-card icon="/assets/icons/history.svg"
                        iconAlt="History"
                        cardTitle="View Logs"
                        description="View diagnostic logs collected during this session."
                        buttonLabel="View Logs"
                        testId="view-logs"
                        ariaLabel="View application logs"
                        (action)="onViewLogsClick()">
      </app-support-card>
    }
    <ng-template #forceSyncProgressTemplate>
      @if (isForceSyncing) {
        <div class="force-sync-progress"
             data-testid="force-sync-progress">
          <p-progressbar [value]="forceSyncProgress"
                         [showValue]="false" />
          @if (recentSyncSteps.length > 0) {
            <div class="force-sync-steps">
              @for (item of displayedSyncSteps; track $index) {
                <span class="force-sync-step-label"
                      [style.opacity]="item.opacity">{{ item.label }}</span>
              }
            </div>
          }
        </div>
      }
    </ng-template>
    <p-message class="support-additional-data"
               data-testid="support-additional-data"
               severity="secondary"
               (click)="copySupportDataToClipboard()"
               [attr.aria-label]="'Copy app version and user ID to clipboard'">
      <small>
        <strong>App Version:</strong> {{ appVersion }}
      </small>
      <small>
        <strong>User ID:</strong> {{ userId.slice(0, 8) }}...{{ userId.slice(-8) }}
      </small>
    </p-message>
  </div>
</ng-template>
<app-log-viewer-modal [(visible)]="isLogViewerOpen">
</app-log-viewer-modal>
<p-confirmdialog />